import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ExternalLink,
  CheckCircle,
  Clock,
  Calendar,
  User,
  ArrowRight,
  Award,
  TrendingUp,
  Zap,
  Target,
  ChevronRight,
  BookOpen,
  Home,
  FileText,
  MapPin,
  AlertTriangle,
  Link as LinkIcon,
  List
} from 'lucide-react';
import { getBlogPost, BLOG_CATEGORIES } from '../data/blogData';
import CmsArticle from '../components/CmsArticle';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const blogData = getBlogPost(slug);
    if (!blogData) {
      setIsLoading(false);
      navigate('/page-not-found', { replace: true });
      return;
    }
    setBlog(blogData);
    setIsLoading(false);
  }, [slug, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Posts from the shared CMS have their own shape and renderer. Resolved synchronously so
  // the prerendered HTML is replaced by identical markup without an empty first render.
  const cmsPost = getBlogPost(slug);
  if (cmsPost && cmsPost.source === 'cms') return <CmsArticle post={cmsPost} />;

  if (isLoading || !blog) return null;

  const category = BLOG_CATEGORIES[blog.category];
  const canonicalUrl = `https://costtoconstruct.com/blog/${blog.slug}`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const year = new Date().getFullYear();
  const isGuide = blog.type === 'guide';

  const buildStructuredData = () => {
    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": blog.title,
      "description": blog.metaDescription,
      "image": "https://costtoconstruct.com/images/blog-cover.jpg",
      "author": { "@type": "Person", "name": blog.author },
      "publisher": {
        "@type": "Organization",
        "name": "Cost to Construction",
        "logo": { "@type": "ImageObject", "url": "https://costtoconstruct.com/logo_large.png" }
      },
      "datePublished": blog.publishDate,
      "dateModified": blog.lastModified,
      "mainEntityOfPage": currentUrl,
      "articleBody": blog.introduction
    };

    const faqPage = blog.faqs ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": blog.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    } : null;

    if (isGuide) {
      return { article, faqPage };
    }

    return {
      article,
      itemList: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": blog.title,
        "description": blog.metaDescription,
        "numberOfItems": blog.tools.length,
        "itemListElement": blog.tools.map((tool, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "SoftwareApplication",
            "name": tool.name,
            "url": tool.url,
            "description": tool.description,
            "applicationCategory": "ConstructionSoftware"
          }
        }))
      },
      faqPage,
      howTo: {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": `How to Choose the Best ${category.label}`,
        "description": `Step-by-step guide to selecting the right ${category.label.toLowerCase()} for your needs`,
        "step": [
          { "@type": "HowToStep", "name": "Identify Your Needs", "text": "Determine your project types, team size, and specific requirements", "position": 1 },
          { "@type": "HowToStep", "name": "Compare Features", "text": "Review the comparison table and feature lists for each tool", "position": 2 },
          { "@type": "HowToStep", "name": "Test Free Trials", "text": "Start with MeltPlan's free tier or request demos from shortlisted tools", "position": 3 },
          { "@type": "HowToStep", "name": "Evaluate ROI", "text": "Calculate time savings and accuracy improvements against subscription costs", "position": 4 },
          { "@type": "HowToStep", "name": "Make Your Decision", "text": "Choose based on your specific needs, with MeltPlan recommended for most users", "position": 5 }
        ]
      }
    };
  };

  const structuredData = buildStructuredData();

  const renderGuideSubsection = (sub, idx) => (
    <div key={idx} className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">{sub.title}</h3>
      {sub.content && <p className="text-gray-700 leading-relaxed mb-4">{sub.content}</p>}
      {sub.links && sub.links.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4">
          {sub.links.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
              <ExternalLink className="mr-1.5" size={14} />
              {link.label}
            </a>
          ))}
        </div>
      )}
      {sub.items && sub.items.length > 0 && (
        <ul className="space-y-2 mb-4">
          {sub.items.map((item, i) => (
            <li key={i} className="flex items-start text-sm text-gray-700">
              <CheckCircle className="mr-2 mt-0.5 text-blue-500 flex-shrink-0" size={16} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {sub.footnote && (
        <p className="text-sm text-gray-500 italic mt-2">
          {sub.footnoteUrl
            ? <a href={sub.footnoteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">{sub.footnote}</a>
            : sub.footnote
          }
        </p>
      )}
    </div>
  );

  const renderGuideSection = (section) => (
    <section key={section.id} id={section.id} className="mb-16">
      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
        <FileText className="mr-3 text-blue-600 flex-shrink-0" size={28} />
        {section.title}
      </h2>

      {section.content && (
        <p className="text-gray-700 leading-relaxed mb-6 text-lg">{section.content}</p>
      )}

      {section.links && section.links.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          {section.links.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium bg-blue-50 px-4 py-2 rounded-lg transition-colors">
              <ExternalLink className="mr-2" size={16} />
              {link.label}
            </a>
          ))}
        </div>
      )}

      {section.items && section.items.length > 0 && (
        <ul className="space-y-2 mb-6">
          {section.items.map((item, i) => (
            <li key={i} className="flex items-start text-gray-700">
              <CheckCircle className="mr-2 mt-0.5 text-blue-500 flex-shrink-0" size={16} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {section.numberedItems && section.numberedItems.length > 0 && (
        <div className="space-y-4 mb-6">
          {section.numberedItems.map((item, i) => (
            <div key={i} className="flex items-start bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm mr-4 flex-shrink-0">{i + 1}</span>
              <div>
                <h4 className="font-bold text-gray-900">{item.label}</h4>
                <p className="text-gray-600 text-sm mt-1">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {section.callout && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mb-6">
          <div className="flex items-start">
            <AlertTriangle className="mr-3 text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-gray-700 text-sm">{section.callout}</p>
          </div>
        </div>
      )}

      {section.footnote && (
        <p className="text-sm text-gray-500 italic mb-6">{section.footnote}</p>
      )}

      {section.subsections && section.subsections.map((sub, idx) => renderGuideSubsection(sub, idx))}
    </section>
  );

  const renderStateLinksTable = () => {
    if (!blog.stateLinks || blog.stateLinks.length === 0) return null;
    return (
      <section id="state-links" className="mb-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
          <MapPin className="mr-3 text-blue-600 flex-shrink-0" size={28} />
          Building Codes by State
        </h2>
        <p className="text-gray-700 mb-6">Direct links to see all codebooks that apply to every US state:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {blog.stateLinks.map((sl, i) => (
            <a key={i} href={sl.url} target="_blank" rel="noopener noreferrer" className="flex items-center bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-blue-300 hover:shadow-md transition-all group">
              <MapPin className="mr-2 text-gray-400 group-hover:text-blue-500 flex-shrink-0" size={16} />
              <span className="text-gray-800 font-medium group-hover:text-blue-600 transition-colors">{sl.state}</span>
              <ExternalLink className="ml-auto text-gray-300 group-hover:text-blue-400" size={14} />
            </a>
          ))}
        </div>
      </section>
    );
  };

  const renderCodebookLinksTable = () => {
    if (!blog.codebookLinks || blog.codebookLinks.length === 0) return null;
    return (
      <section id="codebook-links" className="mb-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
          <BookOpen className="mr-3 text-blue-600 flex-shrink-0" size={28} />
          Model Code Adoption by Codebook
        </h2>
        <p className="text-gray-700 mb-6">See which states have adopted each model code and in what manner:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {blog.codebookLinks.map((cl, i) => (
            <a key={i} href={cl.url} target="_blank" rel="noopener noreferrer" className="flex items-center bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-blue-300 hover:shadow-md transition-all group">
              <BookOpen className="mr-3 text-gray-400 group-hover:text-blue-500 flex-shrink-0" size={16} />
              <span className="text-gray-800 font-medium group-hover:text-blue-600 transition-colors text-sm">{cl.code}</span>
              <ExternalLink className="ml-auto text-gray-300 group-hover:text-blue-400 flex-shrink-0" size={14} />
            </a>
          ))}
        </div>
      </section>
    );
  };

  const renderReferences = () => {
    if (!blog.references || blog.references.length === 0) return null;
    return (
      <section id="references" className="mb-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
          <LinkIcon className="mr-3 text-blue-600 flex-shrink-0" size={28} />
          Authoritative Sources Referenced
        </h2>
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
          {blog.references.map((ref, i) => (
            <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="flex items-center px-6 py-4 hover:bg-blue-50 transition-colors group">
              <ExternalLink className="mr-3 text-gray-400 group-hover:text-blue-500 flex-shrink-0" size={16} />
              <span className="text-gray-800 group-hover:text-blue-600 transition-colors">{ref.label}</span>
              <span className="ml-auto text-xs text-gray-400 group-hover:text-blue-400 truncate max-w-[200px] hidden sm:block">{ref.url}</span>
            </a>
          ))}
        </div>
      </section>
    );
  };

  const renderGuideSidebar = () => (
    <aside className="lg:col-span-1">
      <div className="sticky top-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center">
          <List className="mr-2 text-blue-600" size={20} />
          Table of Contents
        </h3>
        <nav className="space-y-1">
          <a href="#introduction" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
            Introduction
          </a>
          {blog.sections.map((section) => (
            <a key={section.id} href={`#${section.id}`} className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
              {section.title}
            </a>
          ))}
          {blog.stateLinks && blog.stateLinks.length > 0 && (
            <a href="#state-links" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
              Building Codes by State
            </a>
          )}
          {blog.codebookLinks && blog.codebookLinks.length > 0 && (
            <a href="#codebook-links" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
              Code Adoption by Codebook
            </a>
          )}
          {blog.references && blog.references.length > 0 && (
            <a href="#references" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
              Authoritative Sources
            </a>
          )}
          <a href="#faqs" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
            FAQs
          </a>
          <a href="#conclusion" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
            Conclusion
          </a>
        </nav>

        {(blog.stateLinks || blog.codebookLinks) && (
          <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <MapPin className="text-blue-600 mb-2" size={24} />
            <p className="text-sm font-semibold text-gray-900 mb-2">Free Code Lookup Tool</p>
            <p className="text-xs text-gray-600 mb-3">{blog.stateLinks ? 'See all codebooks applicable to your state.' : 'See how model codes are adopted across all states.'}</p>
            <a href="https://www.meltplan.com/buildingcodes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-blue-600 font-semibold hover:text-blue-700">
              Explore Tool <ExternalLink className="ml-1" size={12} />
            </a>
          </div>
        )}
      </div>
    </aside>
  );

  const renderToolsSidebar = () => (
    <aside className="lg:col-span-1">
      <div className="sticky top-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center">
          <BookOpen className="mr-2 text-blue-600" size={20} />
          Quick Navigation
        </h3>
        <nav className="space-y-2">
          <a href="#introduction" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">Introduction</a>
          <a href="#top-10-tools" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">Top 10 Tools Ranking</a>
          <a href="#comparison" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">Comparison Table</a>
          <a href="#faqs" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">FAQs</a>
          <a href="#conclusion" className="block text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">Final Verdict</a>
        </nav>
        <div className="mt-8 p-4 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border border-green-200">
          <Award className="text-green-600 mb-2" size={24} />
          <p className="text-sm font-semibold text-gray-900 mb-2">Our Top Recommendation</p>
          <p className="text-xs text-gray-600">Based on features, ease of use, and value for construction professionals.</p>
        </div>
      </div>
    </aside>
  );

  const renderGuideContent = () => (
    <main className="lg:col-span-3">
      <section id="introduction" className="mb-16">
        <div className="prose prose-lg prose-gray max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed">{blog.introduction}</p>
        </div>
      </section>

      {blog.sections.map(renderGuideSection)}

      {renderStateLinksTable()}
      {renderCodebookLinksTable()}
      {renderReferences()}

      {blog.faqs && blog.faqs.length > 0 && (
        <section id="faqs" className="mb-16">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {blog.faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="conclusion" className="mb-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">Conclusion</h2>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{blog.conclusion}</p>
          </div>
        </div>
      </section>

      {blog.category === 'building-codes-guide' && (
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-white text-center">
        <MapPin className="mx-auto mb-4" size={48} />
        <h3 className="text-2xl font-bold mb-4">Find Your State's Building Codes</h3>
        <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
          Use the free MeltPlan tool to instantly see all codebooks applicable to your state jurisdiction.
        </p>
        <a href="https://www.meltplan.com/buildingcodes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg">
          Explore State Codes
          <ExternalLink className="ml-2" size={20} />
        </a>
      </div>
      )}
    </main>
  );

  const renderToolsContent = () => (
    <main className="lg:col-span-3">
      <section id="introduction" className="mb-16">
        <div className="prose prose-lg prose-gray max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed">{blog.introduction}</p>
        </div>
      </section>

      <section id="tools-overview" className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
          <BookOpen className="mr-3 text-blue-600" size={32} />
          {category.label} - Overview
        </h2>
        <div className="space-y-8">
          {blog.tools.map((tool, index) => (
            <div key={index} id={`tool-${index}`} className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden transition-all duration-300 hover:border-blue-300 hover:shadow-xl">
              <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{tool.name}</h3>
                    <p className="text-lg text-gray-700 mb-3">{tool.tagline}</p>
                    <a href={tool.url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center">
                      {tool.url}
                      <ExternalLink className="ml-2" size={16} />
                    </a>
                  </div>
                  <a href={tool.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-lg">
                    Learn More
                    <ExternalLink className="ml-2" size={16} />
                  </a>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3 text-lg">Overview</h4>
                  <p className="text-gray-700 leading-relaxed">{tool.description}</p>
                </div>
                {tool.keyFeatures && tool.keyFeatures.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                      <Zap className="mr-2 text-blue-600" size={20} />
                      Key Capabilities
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {tool.keyFeatures.map((feature, i) => (
                        <div key={i} className="flex items-start text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          <span className="mr-2 flex-shrink-0">{feature.substring(0, 2)}</span>
                          <span>{feature.substring(3)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tool.strengths && tool.strengths.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-blue-700 mb-3 flex items-center text-lg">
                      <CheckCircle className="mr-2" size={20} />
                      What It Does Well
                    </h4>
                    <ul className="space-y-2">
                      {tool.strengths.map((strength, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="mr-2 mt-0.5 text-blue-500 flex-shrink-0" size={16} />
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {tool.considerations && tool.considerations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center text-lg">
                      <Target className="mr-2" size={20} />
                      Things to Consider
                    </h4>
                    <ul className="space-y-2">
                      {tool.considerations.map((consideration, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <Target className="mr-2 mt-0.5 text-gray-500 flex-shrink-0" size={16} />
                          <span>{consideration}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {tool.idealUseCases && (
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                      <TrendingUp className="mr-2 text-blue-600" size={20} />
                      Typical Use Cases
                    </h4>
                    <p className="text-gray-700 leading-relaxed">{tool.idealUseCases}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {blog.comparisonTable && (
        <section id="comparison" className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Quick Comparison Table</h2>
          <div className="overflow-x-auto bg-white rounded-2xl shadow-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {blog.comparisonTable.headers.map((header, i) => (
                    <th key={i} className="px-4 py-3 text-left text-sm font-bold text-gray-900 border-b border-gray-200">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blog.comparisonTable.rows.map((row, i) => (
                  <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${i === 0 ? 'bg-yellow-50' : ''}`}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section id="faqs" className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {blog.faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
              <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="conclusion" className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          Final Verdict: Best {category.label} in {year}
        </h2>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{blog.conclusion}</p>
          </div>
        </div>
      </section>

      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-white text-center">
        <Award className="mx-auto mb-4" size={48} />
        <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
        <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
          Explore these tools to find the best fit for your construction projects and workflow.
        </p>
        <a href="/" className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg">
          Get Your Free Estimate
          <ArrowRight className="ml-2" size={20} />
        </a>
        <p className="text-blue-200 text-sm mt-4">Free AI-powered construction cost estimates</p>
      </div>
    </main>
  );

  return (
    <>
      <Helmet>
        <title>{blog.metaTitle}</title>
        <meta name="description" content={blog.metaDescription} />
        <meta name="keywords" content={`${category.label}, ${isGuide ? 'building codes, jurisdiction, model codes, state codes, ICC, NFPA' : `best ${category.label.toLowerCase()}, top 10 ${category.label.toLowerCase()}, ${category.label.toLowerCase()} ${year}, construction software, meltplan, estimating tools`}`} />

        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content="https://costtoconstruct.com/images/blog-cover.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.metaDescription} />

        <meta property="article:published_time" content={blog.publishDate} />
        <meta property="article:modified_time" content={blog.lastModified} />
        <meta property="article:author" content={blog.author} />
        <meta property="article:section" content={category.label} />

        <script type="application/ld+json">{JSON.stringify(structuredData.article)}</script>
        {structuredData.itemList && <script type="application/ld+json">{JSON.stringify(structuredData.itemList)}</script>}
        {structuredData.faqPage && <script type="application/ld+json">{JSON.stringify(structuredData.faqPage)}</script>}
        {structuredData.howTo && <script type="application/ld+json">{JSON.stringify(structuredData.howTo)}</script>}

        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className={`bg-gradient-to-br ${isGuide ? 'from-teal-600 via-blue-600 to-indigo-700' : 'from-blue-600 via-indigo-600 to-purple-600'} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <nav className="flex items-center text-sm text-blue-100 mb-6">
              <Home className="mr-2" size={16} />
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="mx-2" size={14} />
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <ChevronRight className="mx-2" size={14} />
              <span className="text-white font-medium">{category.label}</span>
            </nav>

            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-2xl mr-2">{category.icon}</span>
              <span className="font-semibold">{category.label}</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">{blog.title}</h1>

            {isGuide && (
              <p className="text-lg text-blue-100 mb-6 max-w-3xl">
                {blog.subtitle || blog.metaDescription}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-6 text-blue-100">
              <div className="flex items-center">
                <User className="mr-2" size={16} />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="mr-2" size={16} />
                <span>Updated {new Date(blog.lastModified).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center">
                <Clock className="mr-2" size={16} />
                <span>{blog.readTime} read</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {isGuide ? renderGuideSidebar() : renderToolsSidebar()}
            {isGuide ? renderGuideContent() : renderToolsContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
